import { useTranslation } from 'react-i18next';
import { BarChart, HourWeekdayHeatmap, LineChart, tickChars } from '../../components/charts';
import { moneyFormatter, tickMoneyFormatter } from './format';
import { Panel, StatsBody } from './Panel';
import type { Stats } from './stats';
import { useTrend } from './trend';
import type { Remote } from './useRemote';

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

interface CardProps {
    remote: Remote<Stats>;
    className?: string;
}

/** Orders and revenue over time. They have different units, so each gets its own scale. */
export const TrendCard = ({ remote, className }: CardProps) => {
    const { t } = useTranslation('home');
    return (
        <Panel title={t('trend.title')} description={t('trend.hint')} className={className}>
            <StatsBody remote={remote}>
                {(stats) => <Trend stats={stats} />}
            </StatsBody>
        </Panel>
    );
};

const Trend = ({ stats }: { stats: Stats }) => {
    const { t, i18n } = useTranslation('home');
    const trend = useTrend(stats);
    const count = new Intl.NumberFormat(i18n.language).format;
    const money = moneyFormatter(i18n.language, stats.currency);
    const tickMoney = tickMoneyFormatter(i18n.language, stats.currency);
    const axisChars = Math.max(tickChars(Math.max(0, ...trend.orders), count), tickChars(Math.max(0, ...trend.revenue), tickMoney));
    const heading = trend.hourly ? t('trend.hour') : t('trend.day');
    return (
        <div className="grid gap-6">
            <div>
                <h3 className="mb-1 text-small font-semibold text-strong">{t('trend.orders')}</h3>
                <LineChart
                    caption={`${t('trend.orders')}, ${trend.hourly ? t('trend.byHour') : t('trend.byDay')}`}
                    labels={trend.labels}
                    labelHeading={heading}
                    series={[{ key: 'orders', label: t('trend.orders'), values: trend.orders }]}
                    formatValue={count}
                    axisChars={axisChars}
                    area
                    height={180}
                />
            </div>
            <div>
                <h3 className="mb-1 text-small font-semibold text-strong">{t('trend.revenue')}</h3>
                <LineChart
                    caption={`${t('trend.revenue')}, ${trend.hourly ? t('trend.byHour') : t('trend.byDay')}`}
                    labels={trend.labels}
                    labelHeading={heading}
                    series={[{ key: 'revenue', label: t('trend.revenue'), values: trend.revenue }]}
                    formatValue={money}
                    formatTick={tickMoney}
                    axisChars={axisChars}
                    area
                    height={180}
                />
            </div>
        </div>
    );
};

export const HoursCard = ({ remote, className }: CardProps) => {
    const { t, i18n } = useTranslation('home');
    const count = new Intl.NumberFormat(i18n.language).format;
    return (
        <Panel title={t('hours.title')} description={t('hours.hint')} className={className}>
            <StatsBody remote={remote}>
                {(stats) => (
                    <BarChart
                        caption={t('hours.title')}
                        labelHeading={t('hours.hour')}
                        labels={stats.byHour.map((h) => `${h.hour}:00`)}
                        series={[{ key: 'orders', label: t('hours.orders'), values: stats.byHour.map((h) => h.orders) }]}
                        formatValue={count}
                    />
                )}
            </StatsBody>
        </Panel>
    );
};

export const WeekdayCard = ({ remote, className }: CardProps) => {
    const { t, i18n } = useTranslation('home');
    const { t: tc } = useTranslation('charts');
    const count = new Intl.NumberFormat(i18n.language).format;
    return (
        <Panel title={t('weekday.title')} description={t('weekday.hint')} className={className}>
            <StatsBody remote={remote}>
                {(stats) => (
                    <BarChart
                        caption={t('weekday.title')}
                        labelHeading={t('weekday.day')}
                        labels={stats.byWeekday.map((d) => tc(`weekdayShort.${WEEKDAYS[d.weekday - 1]}`))}
                        series={[{ key: 'orders', label: t('hours.orders'), values: stats.byWeekday.map((d) => d.orders) }]}
                        formatValue={count}
                    />
                )}
            </StatsBody>
        </Panel>
    );
};

export const HeatmapCard = ({ remote, className }: CardProps) => {
    const { t, i18n } = useTranslation('home');
    const count = new Intl.NumberFormat(i18n.language).format;
    return (
        <Panel title={t('heatmap.title')} description={t('heatmap.hint')} className={className}>
            <StatsBody remote={remote}>
                {(stats) => <HourWeekdayHeatmap caption={t('heatmap.title')} matrix={stats.heatmap} formatValue={count} />}
            </StatsBody>
        </Panel>
    );
};
